export const CHECKLIST_INPUT_TYPES = ["text", "number", "textarea"];
export const FIELD_RESPONSE_CODES = ["approved", "performed", "deviation", "na"];
export const FIELD_RESPONSE_MODES = ["hidden", "optional", "required"];

export function fieldResponseMode(field, outcome) {
  const configured = field?.response_rules?.[outcome];
  if (FIELD_RESPONSE_MODES.includes(configured)) return configured;
  return ["approved", "performed"].includes(outcome)
    ? (field?.required ? "required" : "optional") : "hidden";
}

export function effectiveInputFields(fields, outcome) {
  return normalizeInputFields(fields).flatMap((field) => {
    const mode = fieldResponseMode(field, outcome);
    return mode === "hidden" ? [] : [{ ...field, required: mode === "required" }];
  });
}

export function normalizeInputFields(raw) {
  if (!Array.isArray(raw) || raw.length === 0) return [];

  const seen = {};
  return raw
    .map((entry) => {
      if (!entry || typeof entry !== "object") return null;
      const label = String(entry.label || "").trim();
      if (!label) return null;

      let key = String(entry.key || "")
        .trim()
        .replace(/\s+/g, "_")
        .toLowerCase();
      if (!key) {
        key = label
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "_")
          .replace(/^_|_$/g, "");
      }
      if (!key) key = "field";
      const base = key;
      let idx = 2;
      while (seen[key]) {
        key = `${base}_${idx}`;
        idx += 1;
      }
      seen[key] = true;

      const inputType = CHECKLIST_INPUT_TYPES.includes(entry.input_type)
        ? entry.input_type
        : "text";

      const next = {
        key,
        label,
        input_type: inputType,
        required: Boolean(entry.required),
        ...(entry.unit ? { unit: String(entry.unit).trim() } : {}),
        ...(entry.placeholder
          ? { placeholder: String(entry.placeholder).trim() }
          : {}),
      };
      if (entry.id != null && String(entry.id).trim() !== "") {
        next.id = entry.id;
      }
      const rules = Object.fromEntries(FIELD_RESPONSE_CODES
        .filter((code) => FIELD_RESPONSE_MODES.includes(entry.response_rules?.[code]))
        .map((code) => [code, entry.response_rules[code]]));
      if (Object.keys(rules).length) next.response_rules = rules;
      return next;
    })
    .filter(Boolean);
}

export function normalizeInputValues(raw, fieldDefs = []) {
  const values =
    raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {};
  const keys = (fieldDefs || []).map((f) => f.key).filter(Boolean);
  if (!keys.length) return {};

  return keys.reduce((acc, key) => {
    const val = values[key];
    if (val != null && String(val).trim() !== "") {
      acc[key] = String(val).trim();
    }
    return acc;
  }, {});
}

export function inputValuesEqual(a, b) {
  return JSON.stringify(a || {}) === JSON.stringify(b || {});
}

/** Merge new documents onto the latest item so in-progress custom fields survive. */
export function withUpdatedDocuments(item, newDocuments) {
  const docs = (Array.isArray(newDocuments) ? newDocuments : []).filter(
    (d) => d?.id != null
  );
  return { ...item, documents: docs };
}

/** Keep the in-progress draft when the server echo was trimmed/stripped. */
export function preferLocalDraftValues(localValues, apiValues) {
  const local =
    localValues && typeof localValues === "object" && !Array.isArray(localValues)
      ? localValues
      : {};
  const api =
    apiValues && typeof apiValues === "object" && !Array.isArray(apiValues)
      ? apiValues
      : {};
  return inputValuesEqual(local, api) ? api : local;
}

export function inputFieldsComplete(fieldDefs, values, outcome = null) {
  const defs = outcome == null ? normalizeInputFields(fieldDefs) : effectiveInputFields(fieldDefs, outcome);
  const vals = normalizeInputValues(values, defs);
  return defs.every((field) => !field.required || Boolean(vals[field.key]));
}

export function missingRequiredInputFields(fieldDefs, values) {
  const defs = normalizeInputFields(fieldDefs);
  const vals = normalizeInputValues(values, defs);
  return defs.filter((field) => field.required && !vals[field.key]);
}

export function newTemplateInputField(label = "") {
  return {
    key: "",
    label,
    input_type: "text",
    required: false,
    unit: "",
    placeholder: "",
  };
}
